import { Button } from '@crowdfpl/ui-web/button';
import { Copy, Check } from 'lucide-react';
import { useState } from 'react';
import { trackUmamiEvent } from '@/helpers/analytics';
import { roundPercentages } from '@/helpers/utils';
import { notify } from '@/services/notifications';

interface ShareButtonsProps {
  duelSlug: string;
  className?: string;
  playerAName?: string;
  playerBName?: string;
  gameweekNumber?: number;
  playerAVotes?: number;
  playerBVotes?: number;
  totalVotes?: number;
  state?: 'active' | 'locked' | 'partial' | 'resolved' | 'cancelled';
  chosenPlayerName?: string | null;
}

export function ShareButtons({
  duelSlug,
  className = '',
  playerAName,
  playerBName,
  gameweekNumber,
  playerAVotes,
  playerBVotes,
  totalVotes,
  state = 'active',
  chosenPlayerName,
}: ShareButtonsProps) {
  // Добавляем tid параметр для предотвращения кэширования
  const timestamp = Date.now();
  const baseUrl = `${window.location.origin}/duel/${duelSlug}`;
  const shareUrl = `${baseUrl}?tid=${timestamp}`;
  const copyUrl = shareUrl;

  const [showCheck, setShowCheck] = useState(false);

  const handleCopyLink = async () => {
    try {
      await navigator.clipboard.writeText(copyUrl);
      trackUmamiEvent('share_copy_link_click', { duelSlug });
      notify.ui.linkCopied();
      setShowCheck(true);
      setTimeout(() => setShowCheck(false), 2000);
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('Failed to copy link:', error);
      notify.ui.linkCopyFailed();
    }
  };

  const createShareText = () => {
    if (!playerAName || !playerBName) {
      return 'Check out this FPL duel! #FPL';
    }

    const isVotingAvailable = state === 'active';
    const hasUserVote = !!chosenPlayerName;
    const hasVotes = totalVotes && totalVotes > 0;

    // Вычисляем проценты с корректным округлением
    const [playerAPercentage, playerBPercentage] = hasVotes && playerAVotes && playerBVotes
      ? roundPercentages(playerAVotes, playerBVotes)
      : [0, 0];

    // Формируем номер геймвика
    const gwText = gameweekNumber && gameweekNumber > 0 ? `GW${gameweekNumber}` : '';

    if (isVotingAvailable) {
      // Дуэль доступна для голосования
      const header = gwText ? `${gwText} Crowd prediction:` : 'Crowd prediction:';
      if (hasUserVote) {
        // Был воут игрока
        return `${header}\n\n${playerAName} ${playerAPercentage}% vs ${playerBName} ${playerBPercentage}%\n\nI'm on ${chosenPlayerName} here. Your choice? #FPL`;
      } else {
        // Не было воута игрока
        return `${header}\n\n${playerAName} ${playerAPercentage}% vs ${playerBName} ${playerBPercentage}%\n\nYour choice? #FPL`;
      }
    } else {
      // Дуэль не доступна для голосования
      const header = gwText ? `${gwText} — What the crowd thought (final votes):` : 'What the crowd thought (final votes):';
      if (hasUserVote) {
        // Был воут игрока
        return `${header}\n\n${playerAName} ${playerAPercentage}% vs ${playerBName} ${playerBPercentage}%\n\nI picked ${chosenPlayerName} — what about you? #FPL`;
      } else {
        // Не было воута игрока
        return `${header}\n\n${playerAName} ${playerAPercentage}% vs ${playerBName} ${playerBPercentage}%\n\n#FPL`;
      }
    }
  };

  const handleXShare = () => {
    trackUmamiEvent('share_x_click', { duelSlug });
    const text = encodeURIComponent(createShareText());
    const url = encodeURIComponent(shareUrl);
    window.open(`https://x.com/intent/tweet?text=${text}&url=${url}`, '_blank');
  };

  return (
    <div className={`flex items-center gap-1 ${className}`}>
      <Button
        variant="ghost"
        size="sm"
        onClick={handleCopyLink}
        className="h-10 w-10 p-0"
        aria-label="Copy duel link to clipboard"
      >
        {showCheck ? <Check className="h-6 w-6" /> : <Copy className="h-6 w-6" />}
      </Button>
      <Button
        variant="ghost"
        size="sm"
        onClick={handleXShare}
        className="h-10 w-10 p-0"
        aria-label="Share duel on X"
      >
        <svg className="h-6 w-6" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
          <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" />
        </svg>
      </Button>
    </div>
  );
}

export default ShareButtons;

